import QuantifierParser from '../../util/QuantifierParser'
import { unitsEqual } from '../../util/quantifier'
import {
	RowType,
	ERow,
	DAStudyGroup,
	DAStudyRow,
	DACell,
	EName,
	EGroup,
	EIntervention,
	ETreatment,
	ECell
} from './types'
import * as lodash from 'lodash'
import type { StringKeyed } from '../../types/util'
const _ = lodash.uniqueId ? lodash : lodash.default

export const isRapidExtract = (g: DAStudyGroup | DAStudyRow | DACell = {}) => /^extract-/.test(g.Source) || !!g.extractor || !!g.client

const CLINICAL_TRIALS_TYPE = 'ClinicalTrials.govs'
export const isClinicalTrialsStudy = metadata => [metadata.InternalMetadata.ImportType, metadata.Metadata.DatabaseType].includes(CLINICAL_TRIALS_TYPE)

export function stringHash(str) {
	let hash = 0, len = str.length
	for (let i = 0; i < len; i++) {
		hash  = ((hash << 5) - hash + str.charCodeAt(i)) << 0
	}
	return hash
}


export const tryParseQuantifier = (value, exact = true) => {
	const results = QuantifierParser.parseText(value, exact)
	return results.length ? results[0].quantifier : undefined
}

export function quantifierToTimepointInterval(q) {
	if (q) {
		const interval = {
			Type: 'Total',
			Gestalt: 'Total',
			FollowUp: {
				Type: {
					Id: 3828,
					Name: 'Maximum',
				},
				TotalValue: {
					High: {
						EqualityOperator: { Name: q.High.Units[0].EqualityOperator },
						Number: q.High.Units[0].Number,
						Unit: q.High.Units[0].Unit
					}
				}
			}
		}
		if (q.Low) {
			interval.FollowUp.TotalValue.Low = {
				EqualityOperator: { Name: q.Low.Units[0].EqualityOperator },
				Number: q.Low.Units[0].Number,
				Unit: q.Low.Units[0].Unit
			}
		}
		if (q.Gestalt) interval.Gestalt = q.Gestalt
		return interval
	}
}

export function rowTime(outcome: ERow) {
	return quantifierToTimepointInterval(outcome.timepoint?.quantifier || tryParseQuantifier(outcome.timepoint?.val))
}

interface InferredTypes {
	rowType: string
	fieldType: string
}

/* Reverse: let type = (row.RowType && row.RowType != 'Standard') ? row.RowType : FieldTypesMap[row.Type] */
export function inferRowTypes(row: DAStudyRow /* RowType | FieldType */, deflt?): InferredTypes {
	const type = row.type
	if (type == 'outcome') {
		throw new Error('unknown outcome type: ' + type)
	}
	let rowType
	let fieldType
	if (!type && row.name == 'Condition' && !row.valueType) {
		// infer String/Text for Condition rows saved without value Type
		return {
			fieldType: 'Text',
		}
	}
	if (!type && deflt) return deflt
	// if omitted, we assume Standard/Continuous
	if (!type || type == 'Standard') {
		fieldType = 'Continuous'
		//rowType = 'Standard'
	} else if (Object.values(RowType).includes(type)) {
		// for now assume all RowTypes imply Continuous FieldType
		fieldType = 'Continuous'
		rowType = type
	} else {
		// it's a field type, so RowType must have been default/'Standard'
		fieldType = type
		//rowType = 'Standard'
	}
	return {
		rowType,
		fieldType
	}
}

export function diffObjects(initial, current, equality = {}) {
	const keys = _.uniq(Object.keys(initial).concat(Object.keys(current)))
	return _.transform(keys, (updated, key) => {
		const equals = equality[key] || _.isEqual
		if (!equals(current[key] ?? undefined, initial[key] ?? undefined)) updated[key] = current[key]
	}, {})
}

/**
 * Helper for applying study metadata updates, being careful to only update specific fields if they are assigned and of
 * the same destination type
 */
export function applyMetadata(target = {}, metadata, overwrite = true, fields = Object.keys(metadata)) {
	let updated = false
	for (const field of fields) {
		const incoming = metadata[field]
		const existing = target[field]
		const incomingIsArray = Array.isArray(incoming)
		const existingIsArray = Array.isArray(existing)
		const incomingIsNull = incoming == null
		const existingIsNull = existing == null
		const incomingHasValue = incomingIsArray ? incoming?.length : !incomingIsNull
		const existingHasValue = existingIsArray ? existing?.length : !existingIsNull
		// values are assignable if either one doesn't have a value
		// or has a value and are both an array or both a not-array
		const sameCardinality = !!(+incomingIsNull ^ +existingIsNull) || !(+(incomingIsArray || incomingIsNull) ^ +(existingIsArray || existingIsNull))
		if (sameCardinality && (!existingHasValue || overwrite) && incomingHasValue) {
			target[field] = incoming
			updated = true
		}
	}
	if (updated) return target
}

export const assignSources = (e, onlyCells = false, overwrite = false) => {
	!onlyCells && _.forEach(e.Data.Groups, g => (!g.Source || overwrite) && (g.Source = 'extract-' + e.Id))
	_.forEach(e.Data.Sections, s =>
		_.forEach(s?.Rows, r => {
			!onlyCells && (!r.Source || overwrite) && (r.Source = 'extract-' + e.Id)
			_.forEach(r.Cells, c => (!c.Source || overwrite) && (c.Source = 'extract-' + e.Id))
		})
	)
	return e
}

export const str = v => (v || '').trim()

export const termSetsMatch = (setsA = [], setsB = []) => {
	if (setsA?.length == 0 && setsB?.length == 0)
		return true
	else
		return setsA?.length != 0 && setsB?.length != 0 &&
	setsA?.some(termSet => setsB?.includes(termSet))
}

const namesMatch = (a: EName, b: EName, labelsSignificant = true) =>
	termSetsMatch(_.compact(a?.term?.senses), _.compact(b?.term?.senses)) && (!labelsSignificant || str(a.val) == str(b.val))

export function diffExtractCell(cur: ECell = {}, initial: ECell = {}) {
	const diff = {}
	if (str(cur.val) != str(initial.val)) {
		diff.val = cur.val
	}
	if (str(cur.n?.val) != str(initial.n?.val)) {
		diff.n = { val: cur.n?.val }
	}
	if (str(cur.sd) != str(initial.sd)) {
		diff.sd = cur.sd
	}
	if (!!cur.pct != !!initial.pct) {
		diff.pct = cur.pct
	}
	return diff
}

export function diffExtractRows(cur: ERow, initial: ERow) {
	const diff = {} as ERow
	// unspecified, true, false
	const sameDirection = cur.isPositive === undefined || (cur.isPositive == initial.isPositive)
	if (!sameDirection) diff.isPositive = cur.isPositive
	// for now just compare objects, don't normalize times
	const sameTime = unitsEqual(cur.timepoint?.val || '', initial.timepoint?.val || '')
	if (!sameTime) diff.timepoint = _.cloneDeep(cur.timepoint)
	const aRowTypes = { rowType: cur.rowType || undefined, fieldType: cur.type || undefined }
	const bRowTypes = { rowType: initial.rowType || undefined, fieldType: initial.type || undefined }
	if (!_.isEqual(aRowTypes, bRowTypes)) {
		diff.rowType = cur.rowType
		diff.type = cur.type
	}
	if (!namesMatch(cur.name, initial.name)) diff.name = _.cloneDeep(cur.name)
	if (cur.definition != initial.definition) {
		diff.definition = cur.definition
	}
	if (cur.method != initial.method) {
		diff.method = cur.method
	}
	if (!_.isEmpty(diff)) {
		diff.id = cur.id
		if (cur.existing) {
			diff.existing = cur.existing
		}
	}
	return diff
}

const treatmentsMatch = (tmta: ETreatment, tmtb: ETreatment) =>
	(tmta?.measure?.val ?? '').trim() == (tmtb?.measure?.val ?? '').trim()

const treatmentListsMatch = (a: ETreatment[], b: ETreatment[]) =>
	a?.length == b?.length && _.differenceWith(a, b, treatmentsMatch).length == 0

const interventionsMatch = (a: EIntervention, b: EIntervention) =>
	namesMatch(a.name, b.name) && treatmentListsMatch(a.treatments, b.treatments)

interface EIdentified {
	id: any
}
/**
 * Performs a diff of member items, preserving membership of unchanged items
 */
export function diffMembers<T extends EIdentified>(current: T[] = [], initial: T[] = [], matches: (a: T, b: T) => boolean, diffItems: (current: T, initial: T) => Partial<T>) {
   const initById: StringKeyed<T> = _.keyBy(initial, e => e.id)
	const changed = current.length != initial.length || _.differenceWith(current, initial, matches).length
	if (changed) {
		const newMembers = current.map(cur => {
			const existing = initById[cur.id]
			if (!existing) {
				return _.cloneDeep(cur)
			}
			const diff = diffItems(cur, existing)
			// preserve the unchanged item membership
			diff.id = cur.id
			return diff
		})
		return newMembers
	}
}

function diffTreatment(tmta: ETreatment, tmtb: ETreatment) {
	const diff: Partial<ETreatment> = {}
	if ((tmta?.measure?.val ?? '').trim() != (tmtb?.measure?.val ?? '').trim()) {
		diff.measure = _.cloneDeep(tmta.measure)
	}
	return diff
}

function diffTreatments(curtmts: ETreatment[], inittmts: ETreatment[] =  []) {
	return diffMembers(curtmts, inittmts, treatmentsMatch, diffTreatment)
}

function diffIntervention(cur: EIntervention, initial: EIntervention) {
	const diff: Partial<EIntervention> = {}
	if (!namesMatch(cur.name, initial.name)) {
		diff.name = cur.name
	}
	if (!treatmentListsMatch(cur.treatments, initial.treatments)) {
		diff.treatments = diffTreatments(cur.treatments, initial.treatments)
	}
	return diff
}

function diffInterventions(curitvns: EIntervention[] = [], inititvns: EIntervention[] = []) {
	return diffMembers(curitvns, inititvns, interventionsMatch, diffIntervention)
}

const itvnToStr = (itvn: EIntervention, orig?: EIntervention) => {
	const text = itvn.name?.val || itvn.name?.term?.label || orig?.name?.term?.label || '<unspecified>'
	let dosages
	if (itvn.treatments) {
		dosages = _.compact(itvn.treatments).map(t => t.measure.val).join(', ')
	} else if (orig?.treatments) {
		dosages = _.compact(orig.treatments).map(t => t.measure.val).join(', ')
	}
	return text + (dosages ? ' ' + dosages : '')
}

export const groupToStr = (group: EGroup, existing?: EGroup) => {
	const itvnsById = _.keyBy(existing?.interventions, itvn => itvn.id)
	// generate group name string from current interventions, if changed, or existing intervention names if names are unchanged/not present
	return group.interventions.map(itvn => itvnToStr(itvn, itvnsById[itvn.id])).join(', ') + (group.subgroup ? (' (' + group.subgroup + ')') : '')
}

export function diffExtractGroups(cur: EGroup, initial: EGroup): EGroup {
	const interventions = diffInterventions(cur.interventions, initial.interventions)
	const group: EGroup = { }
	if (interventions) {
		group.interventions = interventions

		//EV-1769 - Any study with modified interventions gets auto-generated Group Name
		//if (cur.isRE) {
			const name = groupToStr(cur, initial)
			if (name != cur.name) {
				group.name = name
			}
		//}
	}
	if (cur.subgroup != initial.subgroup) {
		group.subgroup = cur.subgroup
	}
	if (!_.isEmpty(group)) {
		group.id = cur.id
		if (cur.existing) {
			group.existing = cur.existing
		}
		if (cur.refid) {
			group.refid = cur.refid
		}
		group.isRE = cur.isRE
		return group
	}
}